import { lazy, Suspense } from 'react';
import { Route, Routes as RouterRoutes } from 'react-router';

import AdminRoute from './AdminRoute';
import LoadingSpinner from '../components/LoadingSpinner';
import ProtectedRoute from './ProtectedRoute';

const About = lazy(() => import('../pages/public/About'));
const AcceptAward = lazy(() => import('../pages/private/AcceptAward'));
const Account = lazy(() => import('../pages/private/Account'));
const AdminHome = lazy(() => import('../admin/AdminHome'));
const AdminAddLogoImage = lazy(() => import('../admin/AddLogoImage'));
const AdminAddOrganization = lazy(() => import('../admin/AddOrganization'));
const AdminAddTemplate = lazy(() => import('../admin/AddTemplate'));
const AdminBulkGrant = lazy(() => import('../admin/BulkGrant'));
const AdminCalendarImport = lazy(() => import('../admin/CalendarImport'));
const AdminEditLogoImage = lazy(() => import('../admin/EditLogoImage'));
const AdminEditOrganization = lazy(() => import('../admin/EditOrganization'));
const AdminListAwards = lazy(() => import('../admin/ListAwards'));
const AdminListCalendar = lazy(() => import('../admin/ListCalendar'));
const AdminListGrantors = lazy(() => import('../admin/ListGrantors'));
const AdminListGrantRequests = lazy(() => import('../admin/ListGrantRequests'));
const AdminListLogoImages = lazy(() => import('../admin/ListLogoImages'));
const AdminListOrgAdmins = lazy(() => import('../admin/ListOrgAdmins'));
const AdminListOrganizations = lazy(() => import('../admin/ListOrganizations'));
const AdminListTeam = lazy(() => import('../admin/ListTeam'));
const AdminListTemplates = lazy(() => import('../admin/ListTemplates'));
const AdminListUsers = lazy(() => import('../admin/ListUsers'));
const AdminStats = lazy(() => import('../admin/Stats'));
const AdminTeamImport = lazy(() => import('../admin/TeamImport'));
const AdminUserDetail = lazy(() => import('../admin/UserDetail'));
const AdminUserEmails = lazy(() => import('../admin/UserEmails'));
const Api = lazy(() => import('../pages/private/Api'));
const AwardDetail = lazy(() => import('../pages/public/AwardDetail'));
const BulkMintForMinters = lazy(() => import('../pages/private/BulkMintForMinters'));
const Calendar = lazy(() => import('../pages/private/Calendar'));
const EmailOptOut = lazy(() => import('../pages/public/EmailOptOut'));
const ForgotUserName = lazy(() => import('../pages/public/ForgotUserName'));
const GettingStarted = lazy(() => import('../pages/public/GettingStarted'));
const ManagedMinting = lazy(() => import('../pages/public/ManagedMinting'));
const Grant = lazy(() => import('../pages/private/Grant'));
const GrantList = lazy(() => import('../pages/private/GrantList'));
const GrantStats = lazy(() => import('../pages/private/GrantStats'));
const Home = lazy(() => import('../pages/private/Home'));
const HowTo = lazy(() => import('../pages/public/HowTo'));
const ListLogoImages = lazy(() => import('../admin/ListLogoImages'));
const ListOrganizations = lazy(() => import('../pages/private/ListOrganizations'));
const Organization = lazy(() => import('../pages/private/Organization'));
const OrgGrantList = lazy(() => import('../pages/private/OrgGrantList'));
const OrgStats = lazy(() => import('../pages/private/OrgStats'));
const Profile = lazy(() => import('../pages/private/Profile'));
const RequestGranting = lazy(() => import('../pages/public/RequestGranting'));
const Revoke = lazy(() => import('../pages/private/Revoke'));
const SignIn = lazy(() => import('../pages/public/SignIn'));
const Team = lazy(() => import('../pages/private/Team'));
const PlayVideo = lazy(() => import('../pages/private/PlayVideo'));
const TrophyLanding = lazy(() => import('../pages/public/TrophyLanding'));
const EngineeringProductivity = lazy(() => import('../pages/public/EngineeringProductivity'));
const NoMatch = lazy(() => import('../pages/public/NoMatch'));
const Privacy = lazy(() => import('../pages/public/Privacy'));
const Terms = lazy(() => import('../pages/public/Terms'));
const Verification = lazy(() => import('../pages/private/Verification'));

// NB that /awardimages and /awardtemplates are not routed below but
// are handled by cloudfront, which points both to an S3 bucket used for
// storing award images and templates.

const stylesheet = {
    suspenseFallback: 'tw:mt-5',
};

function Routes(/* props */) {
    return (
        <Suspense fallback={(
            <div className={stylesheet.suspenseFallback}>
                <LoadingSpinner />
            </div>
          )}
        >
            <RouterRoutes>
                <Route
                  path="/admin"
                  element={<AdminRoute><AdminHome /></AdminRoute>}
                />
                <Route
                  path="/admin/addLogoImage/:organizationId"
                  element={<AdminRoute><AdminAddLogoImage /></AdminRoute>}
                />
                <Route
                  path="/admin/addOrganization"
                  element={<AdminRoute><AdminAddOrganization /></AdminRoute>}
                />
                <Route
                  path="/admin/addTemplate/:organizationId"
                  element={<AdminRoute><AdminAddTemplate /></AdminRoute>}
                />
                <Route
                  path="/admin/awards/:organizationId"
                  element={<AdminRoute><AdminListAwards /></AdminRoute>}
                />
                <Route
                  path="/admin/bulkGrant"
                  element={<AdminRoute><AdminBulkGrant /></AdminRoute>}
                />
                <Route
                  path="/admin/calendarImport"
                  element={<AdminRoute><AdminCalendarImport /></AdminRoute>}
                />
                <Route
                  path="/admin/calendar/:organizationId/:ownerUserName"
                  element={<AdminRoute><AdminListCalendar /></AdminRoute>}
                />
                <Route
                  path="/admin/editLogoImage/:organizationId/:logoImageId"
                  element={<AdminRoute><AdminEditLogoImage /></AdminRoute>}
                />
                <Route
                  path="/admin/editOrganization/:organizationId"
                  element={<AdminRoute><AdminEditOrganization /></AdminRoute>}
                />
                <Route
                  path="/admin/listGrantRequests"
                  element={<AdminRoute><AdminListGrantRequests /></AdminRoute>}
                />
                <Route
                  path="/admin/listLogoImages/:organizationId"
                  element={<AdminRoute><AdminListLogoImages /></AdminRoute>}
                />
                <Route
                  path="/admin/listOrganizations"
                  element={<AdminRoute><AdminListOrganizations /></AdminRoute>}
                />
                <Route
                  path="/admin/minters/:organizationId"
                  element={<AdminRoute><AdminListGrantors /></AdminRoute>}
                />
                <Route
                  path="/admin/stats"
                  element={<AdminRoute><AdminStats /></AdminRoute>}
                />
                <Route
                  path="/admin/team/:organizationId/:managerUserName"
                  element={<AdminRoute><AdminListTeam /></AdminRoute>}
                />
                <Route
                  path="/admin/organizationAdmins/:organizationId"
                  element={<AdminRoute><AdminListOrgAdmins /></AdminRoute>}
                />
                <Route
                  path="/admin/teamImport"
                  element={<AdminRoute><AdminTeamImport /></AdminRoute>}
                />
                <Route
                  path="/admin/templates/:organizationId"
                  element={<AdminRoute><AdminListTemplates /></AdminRoute>}
                />
                <Route
                  path="/admin/user/:userName"
                  element={<AdminRoute><AdminUserDetail /></AdminRoute>}
                />
                <Route
                  path="/admin/userEmails/:userName"
                  element={<AdminRoute><AdminUserEmails /></AdminRoute>}
                />
                <Route
                  path="/admin/users"
                  element={<AdminRoute><AdminListUsers /></AdminRoute>}
                />
                <Route
                  path="/"
                  element={<ProtectedRoute><Home /></ProtectedRoute>}
                />
                <Route
                  path="/acceptaward/:awardId"
                  element={<ProtectedRoute><AcceptAward /></ProtectedRoute>}
                />
                <Route
                  path="/account"
                  element={<ProtectedRoute><Account /></ProtectedRoute>}
                />
                <Route
                  path="/addLogoImage/:organizationId"
                  element={<ProtectedRoute><AdminAddLogoImage /></ProtectedRoute>}
                />
                <Route
                  path="/editLogoImage/:organizationId/:logoImageId"
                  element={<ProtectedRoute><AdminEditLogoImage /></ProtectedRoute>}
                />
                <Route
                  path="/api"
                  element={<ProtectedRoute><Api /></ProtectedRoute>}
                />
                <Route
                  path="/bulkMint"
                  element={<ProtectedRoute><BulkMintForMinters /></ProtectedRoute>}
                />
                <Route
                  path="/calendar"
                  element={<ProtectedRoute><Calendar /></ProtectedRoute>}
                />
                <Route
                  path="/grant"
                  element={<ProtectedRoute><Grant /></ProtectedRoute>}
                />
                <Route
                  path="/grantlist"
                  element={<ProtectedRoute><GrantList /></ProtectedRoute>}
                />
                <Route
                  path="/minters/:organizationId"
                  element={<ProtectedRoute><AdminListGrantors /></ProtectedRoute>}
                />
                <Route
                  path="/grantStats"
                  element={<ProtectedRoute><GrantStats /></ProtectedRoute>}
                />
                <Route
                  path="/listLogoImages/:organizationId"
                  element={<ProtectedRoute><ListLogoImages /></ProtectedRoute>}
                />
                <Route
                  path="/listOrganizations"
                  element={<ProtectedRoute><ListOrganizations /></ProtectedRoute>}
                />
                <Route
                  path="/organizationAwards/:organizationId"
                  element={<ProtectedRoute><OrgGrantList /></ProtectedRoute>}
                />
                <Route
                  path="/organizationStats/:organizationId"
                  element={<ProtectedRoute><OrgStats /></ProtectedRoute>}
                />
                <Route
                  path="/organization/:organizationId"
                  element={<Organization />}
                />
                <Route
                  path="/PlayVideo"
                  element={<ProtectedRoute><PlayVideo /></ProtectedRoute>}
                />
                <Route
                  path="/profile/:profileId"
                  element={<ProtectedRoute><Profile /></ProtectedRoute>}
                />
                <Route
                  path="/revoke"
                  element={<ProtectedRoute><Revoke /></ProtectedRoute>}
                />
                <Route
                  path="/team"
                  element={<ProtectedRoute><Team /></ProtectedRoute>}
                />
                <Route
                  path="/verification/:resourceName/:verificationCode"
                  element={<ProtectedRoute><Verification /></ProtectedRoute>}
                />
                <Route path="/about" element={<About />} />
                <Route path="/award/:awardId" element={<AwardDetail />} />
                <Route path="/award/:awardId/*" element={<AwardDetail />} />
                <Route path="/landingPage" element={<About />} />
                <Route path="/howto" element={<HowTo />} />
                <Route path="/requestGranting" element={<RequestGranting />} />
                <Route path="/TrophyLanding" element={<TrophyLanding />} />
                <Route path="/EngineeringProductivity" element={<EngineeringProductivity />} />
                <Route path="/emailUnsubscribe" element={<EmailOptOut />} />
                <Route path="/forgotusername" element={<ForgotUserName />} />
                <Route path="/gettingStarted" element={<GettingStarted />} />
                <Route path="/managedMinting" element={<ManagedMinting />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/signIn" element={<SignIn />} />
                <Route path="*" element={<NoMatch />} />
            </RouterRoutes>
        </Suspense>
    );
}

export default Routes;
